import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { CustomersDetailsDemoData as agencyInfoDemoData } from '../../../data';
import { useTemplate } from '../../features/template/TemplateProvider';
import useApi from '../../hook/useApi';

/**
 * @typedef {Object} AgencyInfoContextType
 * @property {number} reloadKey
 * @property {(key:number)=>void} setReloadKey
 * @property {Object} agencyInfoData
 * @property {(data:Object)=>void} setAgencyInfoData
 * @property {()=>void} handleReload
 * @property {boolean} showAddressBox
 * @property {()=>void} handleAddressBoxShow
 */

/** @type {import('react').Context<AgencyInfoContextType|null>} */
const AgencyInfoContext = createContext(null);

/**
 * Custom hook to use agency info context
 * @returns {AgencyInfoContextType}
 */
export const useAgencyInfo = () => useContext(AgencyInfoContext);

function AgencyInfoProvider({ agencyId = 0, children }) {
    const { setMessage } = useTemplate();
    const [reloadKey, setReloadKey] = useState(0);
    const [showAddressBox, setShowAddressBox] = useState(false);
    const [agencyInfoData, setAgencyInfoData] = useState(agencyInfoDemoData);
    const { get } = useApi();

    // -- Load all agency initially
    useEffect(() => {
        setAgencyInfoData(agencyInfoDemoData);
        get(`api/agency/${agencyId}/info`)
            .then(({ data, error, message }) => {
                if (error) throw new Error(message);

                // -- Set data
                setAgencyInfoData(data || {});
            })
            .catch((err) => {
                setAgencyInfoData({});
                setMessage({ type: 'error', text: err?.message });
            });
    }, [reloadKey, agencyId]);

    const value = useMemo(() => {
        const handleReload = () => {
            setReloadKey((d) => d + 1);
        };
        const handleAddressBoxShow = () => setShowAddressBox((d) => !d);
        return {
            reloadKey,
            setReloadKey,
            agencyInfoData,
            setAgencyInfoData,
            handleReload,
            showAddressBox,
            handleAddressBoxShow
        };
    }, [reloadKey, agencyInfoData, showAddressBox]);

    return <AgencyInfoContext.Provider value={value}>{children}</AgencyInfoContext.Provider>;
}

export default AgencyInfoProvider;
